import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getActiveTenantId, setActiveTenantId } from '../api/client';
import { useAuth } from './AuthContext';
import type { Membership, TenantId } from '../api';

type TenantState = {
  activeTenantId: string | null;
  active?: Membership;
  selectTenant: (id: TenantId) => void;
};

const TenantCtx = createContext<TenantState | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { memberships } = useAuth();
  const [activeTenantId, setId] = useState<string | null>(getActiveTenantId());

  // Auto-select when exactly one membership exists and none is active yet.
  useEffect(() => {
    if (memberships.length === 1 && activeTenantId == null) {
      const tid = String(memberships[0].tenant_id);
      setActiveTenantId(tid);
      setId(tid);
    }
  }, [memberships, activeTenantId]);

  const selectTenant = (id: TenantId) => {
    const s = String(id);
    setActiveTenantId(s);
    setId(s);
  };

  // Catalyst BIGINT tenant_id overflows Number, so we keep every id as a
  // string end-to-end. Compare directly — both sides are already strings.
  const active = memberships.find((m) => String(m.tenant_id) === activeTenantId);

  return <TenantCtx.Provider value={{ activeTenantId, active, selectTenant }}>{children}</TenantCtx.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}
